// Persistent event-log overlay (Phase 7 Block E).
//
// HTML panel anchored bottom-right. Entries auto-fade after FADE_AFTER_MS.
// Hovering the panel pauses all fades so the player can read history.
// Pure state management lives in event-log-state.ts (no DOM, testable).

import {
  EventLogState,
  FADE_AFTER_MS,
  FADE_DURATION_MS,
  MAX_ENTRIES,
  type EventKind,
  type LogEntry,
} from "./event-log-state.ts";

export type { EventKind };

// Re-export constants so callers don't need to import event-log-state.ts.
export { FADE_AFTER_MS, FADE_DURATION_MS, MAX_ENTRIES };

const KIND_COLOR: Record<EventKind, string> = {
  gather: "#c8c8c8",
  craft: "#88ccff",
  xp: "#88ee88",
  xpTier: "#ffd700",
  market: "#ffe066",
  party: "#cc99ff",
  expedition: "#ffaa55",
  repair: "#aaaaaa",
  error: "#ff6666",
};

// Per-entry DOM handle (not stored on LogEntry — that's pure state).
const elMap = new Map<number, HTMLDivElement>();

export class EventLog {
  private readonly panel: HTMLDivElement;
  private readonly inner: HTMLDivElement;
  private readonly state: EventLogState;

  constructor() {
    this.panel = document.createElement("div");
    this.panel.id = "event-log";
    this.panel.style.cssText = [
      "position:fixed",
      "bottom:80px",
      "right:12px",
      "width:320px",
      "max-height:240px",
      "overflow:hidden",
      "display:flex",
      "flex-direction:column-reverse",
      "gap:2px",
      "pointer-events:auto",
      "z-index:8000",
    ].join(";");

    this.inner = document.createElement("div");
    this.inner.style.cssText = "display:flex;flex-direction:column;gap:2px;";
    this.panel.appendChild(this.inner);

    this.state = new EventLogState((entry, delayMs) => {
      setTimeout(() => this.startFade(entry), delayMs);
    });

    this.panel.addEventListener("mouseenter", () => {
      this.state.setHovered(true);
      this.panel.style.maxHeight = "480px";
      this.panel.style.overflowY = "auto";
    });
    this.panel.addEventListener("mouseleave", () => {
      this.panel.style.maxHeight = "240px";
      this.panel.style.overflowY = "hidden";
      this.state.setHovered(false);
    });

    document.body.appendChild(this.panel);
  }

  push(text: string, kind: EventKind): void {
    const evictedId = this.state.push(text, kind);
    if (evictedId !== null) {
      const el = elMap.get(evictedId);
      if (el) {
        el.remove();
        elMap.delete(evictedId);
      }
    }

    // Build DOM element for the newest entry (push just added it, so at(-1) is always set).
    const entries = this.state.getEntries();
    const entry = entries[entries.length - 1];
    if (entry === undefined) return; // should not happen — push always adds
    const ts = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const el = document.createElement("div");
    el.style.cssText = [
      `color:${KIND_COLOR[kind]}`,
      "font-family:monospace",
      "font-size:11px",
      "line-height:1.4",
      "background:rgba(0,0,0,0.55)",
      "padding:2px 6px",
      "border-radius:3px",
      "transition:opacity " + FADE_DURATION_MS + "ms ease",
      "opacity:1",
      "white-space:nowrap",
      "overflow:hidden",
      "text-overflow:ellipsis",
    ].join(";");
    el.textContent = `[${ts}] ${text}`;
    el.title = text;
    elMap.set(entry.id, el);
    this.inner.appendChild(el);
  }

  private startFade(entry: LogEntry): void {
    const el = elMap.get(entry.id);
    if (!el) return;
    el.style.opacity = "0";
    setTimeout(() => {
      el.remove();
      elMap.delete(entry.id);
      this.state.removeEntry(entry.id);
    }, FADE_DURATION_MS);
  }

  // Exposed for testing via state accessors.
  get count(): number {
    return this.state.count;
  }

  getEntries(): ReadonlyArray<{ text: string; kind: EventKind }> {
    return this.state.getEntries();
  }
}
